import { ResultApiResponse } from 'lib-shared-types-api';
import {
  CreateCustomRoleApiArg,
  CreateCustomRoleApiResponse,
  DeleteCustomRoleApiArg,
  ReadAuthorizationScopesApiResponse,
  ReadRolesApiResponse,
  ReadRoleUsersApiArg,
  ReadRoleUsersApiResponse,
  ReadRoleWithScopesApiArg,
  ReadRoleWithScopesApiResponse,
  UpdateCustomRoleApiArg,
  UpdateCustomRoleApiResponse,
  UpdateCustomRoleScopesApiArg,
  UpdateCustomRoleScopesApiResponse,
  UpdateRoleUsersApiArg,
} from 'lib-shared-user-role';
import { DeepWritable } from 'ts-essentials';

import { api } from 'app/app.api';

import { transformReadRolesResponse } from './utils/transformResponse';

export const rolesApi = api
  .enhanceEndpoints({
    addTagTypes: ['Roles'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      readRoles: builder.query<ReadRolesApiResponse, void>({
        query: () => ({
          version: 1,
          path: 'roles',
        }),
        providesTags: [{ type: 'Roles', id: 'LIST' }],
        transformResponse: transformReadRolesResponse,
      }),
      readRoleWithScopes: builder.query<
        ReadRoleWithScopesApiResponse,
        ReadRoleWithScopesApiArg
      >({
        query: ({ roleId, isSystemRole }) => ({
          version: 1,
          path: `roles/${roleId}`,
          params: {
            is_system_role: isSystemRole,
          },
        }),
      }),
      readRoleUsers: builder.query<
        ReadRoleUsersApiResponse,
        ReadRoleUsersApiArg
      >({
        query: ({ roleId }) => ({
          version: 1,
          path: `roles/${roleId}/users`,
        }),
      }),
      updateRoleUsers: builder.mutation<
        ResultApiResponse,
        UpdateRoleUsersApiArg
      >({
        query: ({ roleId, userAdditions, userDeletions }) => ({
          version: 1,
          path: `roles/${roleId}/users`,
          method: 'PATCH',
          body: {
            user_additions: userAdditions,
            user_deletions: userDeletions,
          },
        }),
      }),
      createCustomRole: builder.mutation<
        CreateCustomRoleApiResponse,
        CreateCustomRoleApiArg
      >({
        query: (body) => ({
          version: 1,
          path: 'roles',
          method: 'POST',
          body,
        }),
        invalidatesTags: [{ type: 'Roles', id: 'LIST' }],
      }),
      updateCustomRole: builder.mutation<
        UpdateCustomRoleApiResponse,
        UpdateCustomRoleApiArg
      >({
        query: ({ roleId, data }) => ({
          version: 1,
          path: `roles/${roleId}`,
          method: 'PUT',
          body: data,
        }),
        async onQueryStarted({ roleId, data }, { dispatch, queryFulfilled }) {
          const patchReadRoleWithScopes = dispatch(
            rolesApi.util.updateQueryData(
              'readRoleWithScopes',
              { roleId, isSystemRole: false },
              (draft) => {
                const mutableData = draft.data as DeepWritable<
                  typeof draft.data
                >;

                mutableData.role.name = data.name;
                mutableData.role.description = data.description;
              },
            ),
          );

          try {
            await queryFulfilled;
          } catch {
            patchReadRoleWithScopes.undo();
          }
        },
        invalidatesTags: [{ type: 'Roles', id: 'LIST' }],
      }),
      updateCustomRoleScopes: builder.mutation<
        UpdateCustomRoleScopesApiResponse,
        UpdateCustomRoleScopesApiArg
      >({
        query: ({ roleId, scopeAdditions, scopeDeletions }) => ({
          version: 1,
          path: `roles/${roleId}/scopes`,
          method: 'PATCH',
          body: {
            scope_additions: scopeAdditions,
            scope_deletions: scopeDeletions,
          },
        }),
        async onQueryStarted({ roleId }, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;

            dispatch(
              rolesApi.util.updateQueryData(
                'readRoleWithScopes',
                { roleId, isSystemRole: false },
                (draft) => {
                  const mutableData = draft.data as DeepWritable<
                    typeof draft.data
                  >;

                  mutableData.role_scopes = data.data
                    .role_scopes as DeepWritable<typeof data.data.role_scopes>;
                },
              ),
            );
          } catch {}
        },
      }),
      deleteCustomRole: builder.mutation<
        ResultApiResponse,
        DeleteCustomRoleApiArg
      >({
        query: ({ roleId }) => ({
          version: 1,
          path: `roles/${roleId}`,
          method: 'DELETE',
        }),
        invalidatesTags: [{ type: 'Roles', id: 'LIST' }],
      }),
      readAuthorizationScopes: builder.query<
        ReadAuthorizationScopesApiResponse,
        void
      >({
        query: () => ({
          version: 1,
          path: 'authorization-scopes',
        }),
      }),
    }),
  });
